import { useMutation, useQueryClient, useSuspenseQuery, useQuery } from '@tanstack/react-query'
import { axiosInstance } from '@/api/axios'
import { createQueryKey, PROVISIONS_BASE_KEY, FINANCIAL_MODEL_SETTINGS_BASE_KEY, invalidateGlobalsQueryKey } from '@/api/query-keys'
import { ConfigType, PostProvisionsSetup, ProvisionsSetup, UpsertProvisionCategory } from '../types/setup'
import {
  BadDebtPeriodDataPost,
  BadDebtPeriodDataResponse,
  ProvisionInventoryPeriodDataResponse,
  ProvisionInventoryPeriodDataPostV2,
  ProvisionInventoryAllowedTypes,
  ProvisionInventoryBaseFieldsKeys,
  BadDebtBaseFieldsKeys,
  BadDebtAllowedTypes,
  OtherProvisionsPeriodDataPost,
  OtherProvisionsPeriodDataResponse,
  OtherProvisionsAllowedTypes,
  OtherProvisionsBaseFieldsKeys
} from '../types'
import { PeriodTypeQuery, PeriodTypeView } from '@/types'
import { IntermediaryProvisionsTotals, ProvisionsTotalResults } from '../types/totals'
import { parseProvisionsTotalsResults } from './parsers'

const PROVISIONS_SETUP_KEY = createQueryKey({
  baseKey: PROVISIONS_BASE_KEY,
  keys: [`setup`]
})

export const PROVISIONS_BAD_DEBT_KEY = createQueryKey({
  baseKey: PROVISIONS_BASE_KEY,
  keys: [`bad-debt`]
})

export const PROVISIONS_INVENTORY_KEY = createQueryKey({
  baseKey: PROVISIONS_BASE_KEY,
  keys: [`inventory`]
})

export const PROVISIONS_OTHER_KEY = createQueryKey({
  baseKey: PROVISIONS_BASE_KEY,
  keys: [`other`]
})

export const PROVISIONS_TOTALS_KEY = createQueryKey({
  baseKey: PROVISIONS_BASE_KEY,
  keys: [`totals`]
})

const getProvisionsSetup = async (financial_model_id: string) => {
  const { data } = await axiosInstance.get<ProvisionsSetup>(`/financial-models/${financial_model_id}/provisions/setup`)

  return data
}

export const useGetProvisionsSetup = (financial_model_id: string) =>
  useSuspenseQuery({
    queryKey: PROVISIONS_SETUP_KEY.concat([financial_model_id]),
    queryFn: () => getProvisionsSetup(financial_model_id),
    select: data => {
      const configTypes = Object.keys(data) as ConfigType[]
      const openItems = configTypes.filter(config_type => data[config_type].is_enabled)

      return {
        ...data,
        openItems,
        configTypes
      }
    }
  })

type UpdateProvisionsSetupParams = {
  financial_model_id: string
  data: PostProvisionsSetup
}

const updateProvisionsSetup = async ({ financial_model_id, data }: UpdateProvisionsSetupParams) => {
  const { data: response } = await axiosInstance.post<ProvisionsSetup>(
    `/financial-models/${financial_model_id}/provisions/setup/${data.config_type}`,
    data
  )

  return response
}

export const useUpdateProvisionsSetup = ({ financial_model_id }: Pick<UpdateProvisionsSetupParams, 'financial_model_id'>) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: PostProvisionsSetup) => updateProvisionsSetup({ financial_model_id, data }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: PROVISIONS_SETUP_KEY.concat([financial_model_id])
      })
      queryClient.invalidateQueries({
        queryKey: PROVISIONS_BAD_DEBT_KEY.concat([financial_model_id])
      })
      queryClient.invalidateQueries({
        queryKey: PROVISIONS_INVENTORY_KEY.concat([financial_model_id])
      })
      queryClient.invalidateQueries({
        queryKey: PROVISIONS_OTHER_KEY.concat([financial_model_id])
      })
      queryClient.resetQueries({
        queryKey: PROVISIONS_TOTALS_KEY
      })
      invalidateGlobalsQueryKey([FINANCIAL_MODEL_SETTINGS_BASE_KEY])
    }
  })
}

type UpsertProvisionCategoryParams = {
  financial_model_id: string
  config_type: ConfigType
  data: UpsertProvisionCategory
}

const upsertProvisionCategory = async ({ financial_model_id, config_type, data }: UpsertProvisionCategoryParams) => {
  const { data: response } = await axiosInstance.post<ProvisionsSetup>(
    `/financial-models/${financial_model_id}/provisions/setup/${config_type}/categories`,
    data
  )

  return response
}

export const useUpsertProvisionCategory = ({
  financial_model_id,
  config_type
}: Pick<UpsertProvisionCategoryParams, 'financial_model_id' | 'config_type'>) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UpsertProvisionCategory) => upsertProvisionCategory({ financial_model_id, config_type, data }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: PROVISIONS_SETUP_KEY.concat([financial_model_id])
      })
      queryClient.invalidateQueries({
        queryKey: PROVISIONS_BAD_DEBT_KEY.concat([financial_model_id])
      })
      queryClient.invalidateQueries({
        queryKey: PROVISIONS_INVENTORY_KEY.concat([financial_model_id])
      })
      queryClient.invalidateQueries({
        queryKey: PROVISIONS_OTHER_KEY.concat([financial_model_id])
      })
      queryClient.resetQueries({
        queryKey: PROVISIONS_TOTALS_KEY
      })
      invalidateGlobalsQueryKey([FINANCIAL_MODEL_SETTINGS_BASE_KEY])
    }
  })
}

type DeleteProvisionCategoryParams = {
  financial_model_id: string
  config_type: ConfigType
  category_id: string
}

const deleteProvisionCategory = async ({ financial_model_id, config_type, category_id }: DeleteProvisionCategoryParams) => {
  const { data: response } = await axiosInstance.delete<ProvisionsSetup>(
    `/financial-models/${financial_model_id}/provisions/setup/${config_type}/categories/${category_id}`
  )

  return response
}

export const useDeleteProvisionCategory = ({
  financial_model_id,
  config_type
}: Pick<DeleteProvisionCategoryParams, 'financial_model_id' | 'config_type'>) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (category_id: string) => deleteProvisionCategory({ financial_model_id, config_type, category_id }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: PROVISIONS_SETUP_KEY.concat([financial_model_id])
      })
      queryClient.invalidateQueries({
        queryKey: PROVISIONS_BAD_DEBT_KEY.concat([financial_model_id])
      })
      queryClient.invalidateQueries({
        queryKey: PROVISIONS_INVENTORY_KEY.concat([financial_model_id])
      })
      queryClient.invalidateQueries({
        queryKey: PROVISIONS_OTHER_KEY.concat([financial_model_id])
      })
      queryClient.resetQueries({
        queryKey: PROVISIONS_TOTALS_KEY
      })
      invalidateGlobalsQueryKey([FINANCIAL_MODEL_SETTINGS_BASE_KEY])
    }
  })
}

type GetProvisionsDataEntryParams = {
  financial_model_id: string
  type: PeriodTypeQuery
  view: PeriodTypeView
}

const getProvisionsBadDebtDataEntry = async ({ financial_model_id, type, view }: GetProvisionsDataEntryParams) => {
  const { data } = await axiosInstance.get<BadDebtPeriodDataResponse>(`/financial-models/${financial_model_id}/provisions/data-entry/bad_debt`, {
    params: { type, view }
  })

  return data
}

export const useGetProvisionsBadDebtDataEntry = ({ financial_model_id, type, view }: GetProvisionsDataEntryParams) =>
  useSuspenseQuery({
    queryKey: PROVISIONS_BAD_DEBT_KEY.concat([financial_model_id, type, view]),
    queryFn: () => getProvisionsBadDebtDataEntry({ financial_model_id, type, view })
  })

export const useGetBadDebtHistoricalDataQuery = ({ financial_model_id, view }: Omit<GetProvisionsDataEntryParams, 'type'>) =>
  useQuery({
    queryKey: PROVISIONS_BAD_DEBT_KEY.concat([financial_model_id, `historical`, view]),
    queryFn: () => getProvisionsBadDebtDataEntry({ financial_model_id, type: `historical`, view }),
    enabled: Boolean(financial_model_id)
  })

const updateBadDebtPeriodData = async (financialModelId: string, payload: BadDebtPeriodDataPost) => {
  const { data } = await axiosInstance.post(`/financial-models/${financialModelId}/provisions/data-entry/bad_debt`, payload)

  return data
}

export const useUpdateBadDebtPeriodData = (financialModelId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: BadDebtPeriodDataPost) => updateBadDebtPeriodData(financialModelId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: PROVISIONS_BAD_DEBT_KEY.concat([financialModelId])
      })
      queryClient.resetQueries({
        queryKey: PROVISIONS_TOTALS_KEY
      })
      invalidateGlobalsQueryKey([FINANCIAL_MODEL_SETTINGS_BASE_KEY])
    }
  })
}

export const useBulkUpdateBadDebtPeriodData = (financialModelId: string) =>
  useMutation({
    mutationFn: (payload: BadDebtPeriodDataPost) => updateBadDebtPeriodData(financialModelId, payload)
  })

const getProvisionsInventoryDataEntry = async ({ financial_model_id, type, view }: GetProvisionsDataEntryParams) => {
  const { data } = await axiosInstance.get<ProvisionInventoryPeriodDataResponse>(
    `/financial-models/${financial_model_id}/provisions/data-entry/inventory`,
    { params: { type, view } }
  )

  return data
}

export const useGetProvisionsInventoryDataEntry = ({ financial_model_id, type, view }: GetProvisionsDataEntryParams) =>
  useSuspenseQuery({
    queryKey: PROVISIONS_INVENTORY_KEY.concat([financial_model_id, type, view]),
    queryFn: () => getProvisionsInventoryDataEntry({ financial_model_id, type, view })
  })

export const useGetProvisionInventoryHistoricalDataQuery = ({ financial_model_id, view }: Omit<GetProvisionsDataEntryParams, 'type'>) =>
  useQuery({
    queryKey: PROVISIONS_INVENTORY_KEY.concat([financial_model_id, `historical`, view]),
    queryFn: () => getProvisionsInventoryDataEntry({ financial_model_id, type: `historical`, view }),
    enabled: Boolean(financial_model_id)
  })

const updateInventoryPeriodData = async (financialModelId: string, payload: ProvisionInventoryPeriodDataPostV2) => {
  const { data } = await axiosInstance.post(`/financial-models/${financialModelId}/provisions/data-entry/inventory`, payload)

  return data
}

export const useUpdateInventoryPeriodData = (financialModelId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: ProvisionInventoryPeriodDataPostV2) => updateInventoryPeriodData(financialModelId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: PROVISIONS_INVENTORY_KEY.concat([financialModelId])
      })
      queryClient.resetQueries({
        queryKey: PROVISIONS_TOTALS_KEY
      })
      invalidateGlobalsQueryKey([FINANCIAL_MODEL_SETTINGS_BASE_KEY])
    }
  })
}

export const useBulkUpdateInventoryPeriodData = (financialModelId: string) =>
  useMutation({
    mutationFn: (payload: ProvisionInventoryPeriodDataPostV2) => updateInventoryPeriodData(financialModelId, payload)
  })

const getOtherProvisionsDataEntry = async ({ financial_model_id, type, view }: GetProvisionsDataEntryParams) => {
  const { data } = await axiosInstance.get<OtherProvisionsPeriodDataResponse>(`/financial-models/${financial_model_id}/provisions/data-entry/other`, {
    params: { type, view }
  })

  return data
}

export const useGetOtherProvisionsDataEntry = ({ financial_model_id, type, view }: GetProvisionsDataEntryParams) =>
  useSuspenseQuery({
    queryKey: PROVISIONS_OTHER_KEY.concat([financial_model_id, type, view]),
    queryFn: () => getOtherProvisionsDataEntry({ financial_model_id, type, view })
  })

export const useGetOtherProvisionsHistoricalDataQuery = ({ financial_model_id, view }: Omit<GetProvisionsDataEntryParams, 'type'>) =>
  useQuery({
    queryKey: PROVISIONS_OTHER_KEY.concat([financial_model_id, `historical`, view]),
    queryFn: () => getOtherProvisionsDataEntry({ financial_model_id, type: `historical`, view }),
    enabled: Boolean(financial_model_id)
  })

const updateOtherProvisionsPeriodData = async (financialModelId: string, payload: OtherProvisionsPeriodDataPost) => {
  const { data } = await axiosInstance.post(`/financial-models/${financialModelId}/provisions/data-entry/other`, payload)

  return data
}

export const useUpdateOtherProvisionsPeriodData = (financialModelId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: OtherProvisionsPeriodDataPost) => updateOtherProvisionsPeriodData(financialModelId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: PROVISIONS_OTHER_KEY.concat([financialModelId])
      })
      queryClient.resetQueries({
        queryKey: PROVISIONS_TOTALS_KEY
      })
      invalidateGlobalsQueryKey([FINANCIAL_MODEL_SETTINGS_BASE_KEY])
    }
  })
}

export const useBulkUpdateOtherProvisionsPeriodData = (financialModelId: string) =>
  useMutation({
    mutationFn: (payload: OtherProvisionsPeriodDataPost) => updateOtherProvisionsPeriodData(financialModelId, payload)
  })

type SwitchProvisionColumnTypeParams = {
  financial_model_id: string
  input_type: ProvisionInventoryAllowedTypes | BadDebtAllowedTypes | OtherProvisionsAllowedTypes
  column_name: ProvisionInventoryBaseFieldsKeys | BadDebtBaseFieldsKeys | OtherProvisionsBaseFieldsKeys
  setup_id: string
  month?: number
  year?: number
  product_id?: string | null
  category_id?: string | null
}

const switchProvisionColumnType = async (params: SwitchProvisionColumnTypeParams) => {
  const { financial_model_id, column_name, ...rest } = params
  const { data } = await axiosInstance.post(`/financial-models/${financial_model_id}/provisions/config/${column_name}`, undefined, { params: rest })

  return data
}

export const useSwitchProvisionColumnType = (financial_model_id: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: SwitchProvisionColumnTypeParams) => switchProvisionColumnType(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: PROVISIONS_SETUP_KEY.concat([financial_model_id])
      })
      queryClient.invalidateQueries({
        queryKey: PROVISIONS_BAD_DEBT_KEY.concat([financial_model_id])
      })
      queryClient.invalidateQueries({
        queryKey: PROVISIONS_INVENTORY_KEY.concat([financial_model_id])
      })
      queryClient.invalidateQueries({
        queryKey: PROVISIONS_OTHER_KEY.concat([financial_model_id])
      })
      queryClient.resetQueries({
        queryKey: PROVISIONS_TOTALS_KEY
      })
      invalidateGlobalsQueryKey([FINANCIAL_MODEL_SETTINGS_BASE_KEY])
    }
  })
}

type GetIntermediaryProvisionsTotalsParams = {
  financial_model_id: string
  config_type: ConfigType
  type: PeriodTypeQuery
  view: PeriodTypeView
}

const getIntermediaryProvisionsTotals = async ({ financial_model_id, config_type, type, view }: GetIntermediaryProvisionsTotalsParams) => {
  const { data } = await axiosInstance.get<IntermediaryProvisionsTotals>(`/financial-models/${financial_model_id}/provisions/totals/${config_type}`, {
    params: { type, view }
  })

  return data
}

export const useGetIntermediaryProvisionsTotals = ({ financial_model_id, config_type, type, view }: GetIntermediaryProvisionsTotalsParams) =>
  useSuspenseQuery({
    queryKey: PROVISIONS_TOTALS_KEY.concat([financial_model_id, config_type, type, view]),
    queryFn: () =>
      getIntermediaryProvisionsTotals({
        financial_model_id,
        config_type,
        type,
        view
      })
  })

type GetProvisionsTotalsResultsParams = {
  financial_model_id: string
  type: PeriodTypeQuery
  view: PeriodTypeView
}

const getProvisionsTotalsResults = async ({ financial_model_id, type, view }: GetProvisionsTotalsResultsParams) => {
  const { data } = await axiosInstance.get<ProvisionsTotalResults>(`/financial-models/${financial_model_id}/provisions/totals`, {
    params: { type, view }
  })

  return data
}

export const useGetProvisionsTotalsResults = ({ financial_model_id, type, view }: GetProvisionsTotalsResultsParams) =>
  useSuspenseQuery({
    queryKey: PROVISIONS_TOTALS_KEY.concat([financial_model_id, type, view]),
    queryFn: () => getProvisionsTotalsResults({ financial_model_id, type, view }),
    select: parseProvisionsTotalsResults
  })
